import { useCallback, useMemo } from "react";

import {
	useGetSendersUsersQuery,
	useLazyCheckLinkedinConnectionQuery,
} from "@/pages/Private/redux/admin/admin.api";

import { SenderRecipientOption } from "../components/SenderRecipientDropdown";

import { buildSenderOptions } from "./buildSenderOptions.util";
import { isLastSenderConnectedTo, resolveLastSenderUserId } from "./lastSender.util";
import { draftWaitsForAcceptance } from "./pendingInvitation.util";
import { ConnectionCheckLead, useLinkedinConnectionCheck } from "./useLinkedinConnectionCheck";

import type { LinkedinSenderStates } from "./filterLinkedinSenders.util";
import type { Draft } from "./inboxDraft.types";
import type { User } from "@/pages/Public/pages/Login/schema/login";

/** The slice of a lead the composer's sender logic reads. */
export interface ComposerSendersLead extends ConnectionCheckLead {
	prospect?: (NonNullable<ConnectionCheckLead["prospect"]> & { email?: string | null }) | null;
}

export interface ComposerLinkedinSenders {
	/** The `users/senders` answer, as the composer's other helpers take it. */
	sendingUsers: { data?: User[] } | undefined;
	connectedUserIds: Set<number>;
	senderStates: LinkedinSenderStates;
	/** Senders whose own invitation to the lead is pending, for the GenerateBar (PREC-4216). */
	pendingInvitationUserIds: Set<number>;
	connectionCheckDone: boolean;
	recheck: () => void;
	lastSenderUserId: number | undefined;
	isLastSenderConnected: boolean | undefined;
	senderOptions: SenderRecipientOption[];
	/** Whether a draft waits for its sender's pending invitation to be accepted (PREC-4216). */
	waitsForAcceptance: (draft: Draft | undefined) => boolean;
}

/**
 * The composer's senders for one lead (extracted from `useAddInboxChat`, which sits far over the
 * length limit): the company's sending users, what each LinkedIn sender can do with the lead
 * (connected, its own invitation pending, unknown after a failed check; PREC-4216), the
 * conversation's last sender, and the sender options the "From" picker offers, labelled by
 * those states.
 */
export const useComposerLinkedinSenders = (
	lead: ComposerSendersLead | undefined,
	currentUserId: number | undefined
): ComposerLinkedinSenders => {
	const { data: sendingUsers } = useGetSendersUsersQuery();
	const [checkConnection] = useLazyCheckLinkedinConnectionQuery();
	const { connectedUserIds, senderStates, connectionCheckDone, recheck } =
		useLinkedinConnectionCheck({ lead, sendingUsers: sendingUsers?.data, checkConnection });

	const lastSenderUserId = useMemo(
		() => resolveLastSenderUserId(lead?.prospect?.conversations),
		[lead]
	);

	const senderOptions: SenderRecipientOption[] = useMemo(
		() =>
			sendingUsers?.data
				? buildSenderOptions({
						users: sendingUsers.data,
						connectionCheckDone,
						connectedUserIds,
						senderStates,
						prospectHasEmail: !!lead?.prospect?.email,
						prospectHasLinkedinUrl: !!lead?.prospect?.linkedinUrl,
						currentUserId,
					})
				: [],
		[sendingUsers, connectionCheckDone, connectedUserIds, senderStates, lead, currentUserId]
	);

	const pendingInvitationUserIds = useMemo(
		() => new Set(senderStates.pendingInvitations.keys()),
		[senderStates]
	);

	const waitsForAcceptance = useCallback(
		(draft: Draft | undefined) => draftWaitsForAcceptance(draft, senderStates.pendingInvitations),
		[senderStates]
	);

	return {
		sendingUsers,
		connectedUserIds,
		senderStates,
		pendingInvitationUserIds,
		connectionCheckDone,
		recheck,
		lastSenderUserId,
		isLastSenderConnected: isLastSenderConnectedTo(lastSenderUserId, connectedUserIds),
		senderOptions,
		waitsForAcceptance,
	};
};
